"use client";

import { useEffect, useState } from "react";
import { Ban, Check, Circle, Loader2 } from "lucide-react";

import { cn } from "@/shared/lib/utils/cn";
import type { InsightsTaskItem, InsightsTaskList } from "../types";
import { elapsedMsBetween, formatElapsedMs } from "./format-elapsed";

function TaskDuration({
  task,
  runLive = true,
}: {
  task: InsightsTaskItem;
  /** False when the investigation run has ended — freeze in_progress clocks. */
  runLive?: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  const ticking =
    runLive && task.status === "in_progress" && Boolean(task.runningSince) && !task.pausedAt;

  useEffect(() => {
    if (!ticking) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [ticking, task.runningSince]);

  if (task.status === "blocked" || task.status === "skipped") {
    return (
      <span className="shrink-0 text-[11px] font-medium text-muted-foreground">
        {task.status === "blocked" ? "Blocked" : "Skipped"}
      </span>
    );
  }

  if (task.status === "completed" && task.durationMs != null) {
    return (
      <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
        {formatElapsedMs(task.durationMs)}
      </span>
    );
  }

  if (task.status === "in_progress") {
    const base = task.activeMs ?? 0;
    const ms =
      !runLive || task.pausedAt
        ? base > 0
          ? base
          : task.durationMs != null
            ? task.durationMs
            : null
        : task.runningSince
          ? base + Math.max(0, now - Date.parse(task.runningSince))
          : task.startedAt
            ? Math.max(0, now - Date.parse(task.startedAt))
            : null;
    if (ms != null && Number.isFinite(ms)) {
      return (
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {formatElapsedMs(ms)}
        </span>
      );
    }
  }

  if (task.status === "completed" && task.startedAt && task.completedAt) {
    const ms = elapsedMsBetween(task.startedAt, task.completedAt);
    if (ms != null) {
      return (
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {formatElapsedMs(ms)}
        </span>
      );
    }
  }

  return null;
}

export function InsightsTaskListPanel({
  taskList,
  hasCoverageBlockers = false,
  onTaskClick,
  variant = "card",
  runLive = true,
}: {
  taskList: InsightsTaskList | null;
  hasCoverageBlockers?: boolean;
  /** Scroll the matching chat task panel into view. */
  onTaskClick?: (taskId: string) => void;
  /** `plain` for investigation sidebar sections (no card chrome). */
  variant?: "card" | "plain";
  /** False when the investigation run has ended — stop spinners on open tasks. */
  runLive?: boolean;
}) {
  if (!taskList || taskList.tasks.length === 0) return null;

  const completed =
    taskList.summary?.completed ?? taskList.tasks.filter((t) => t.status === "completed").length;
  const total = taskList.summary?.total ?? taskList.tasks.length;

  return (
    <aside
      className={
        variant === "plain"
          ? "flex min-h-0 w-full flex-col"
          : "flex h-full min-h-0 w-full flex-col rounded-2xl border border-border/70 bg-background/80 p-4"
      }
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">Tasks</h2>
        <span className="text-xs text-muted-foreground">
          {completed}/{total}
        </span>
      </div>
      <ul className="min-h-0 space-y-0.5 pr-1">
        {taskList.tasks.map((task) => {
          const effectiveStatus =
            task.status === "completed" &&
            (task.durationMs === 0 || task.durationMs == null) &&
            hasCoverageBlockers
              ? "blocked"
              : !runLive && task.status === "in_progress"
                ? "skipped"
                : task.status;

          return (
            <li key={task.id}>
              <button
                type="button"
                data-task-id={task.id}
                onClick={() => onTaskClick?.(task.id)}
                className={cn(
                  "flex w-full items-start gap-2 rounded-md border px-1.5 py-1 text-left text-[13px] transition-colors",
                  onTaskClick && "hover:bg-muted/50",
                  effectiveStatus === "in_progress"
                    ? "border-primary/35 bg-primary/5 shadow-sm"
                    : "border-transparent",
                  (effectiveStatus === "completed" ||
                    effectiveStatus === "blocked" ||
                    effectiveStatus === "skipped") &&
                    "opacity-70",
                )}
              >
                {effectiveStatus === "completed" ? (
                  <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_oklch,var(--success)_14%,var(--card))] text-success">
                    <Check className="size-2.5" strokeWidth={3} />
                  </span>
                ) : effectiveStatus === "in_progress" ? (
                  <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin text-primary" />
                ) : effectiveStatus === "blocked" || effectiveStatus === "skipped" ? (
                  <Ban className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                ) : (
                  <Circle className="mt-0.5 size-4 shrink-0 text-muted-foreground/50" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div
                      className={cn(
                        "min-w-0 leading-snug",
                        effectiveStatus === "completed" && "text-muted-foreground line-through",
                        effectiveStatus === "in_progress" && "font-medium text-foreground",
                      )}
                    >
                      {task.title}
                    </div>
                    <TaskDuration
                      runLive={runLive}
                      task={
                        effectiveStatus === "blocked" || effectiveStatus === "skipped"
                          ? { ...task, status: effectiveStatus }
                          : task
                      }
                    />
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
